import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createDailyPrivateShareCardBlob } from '../../../utils/dailyPrivateShareCard';
import { shareOrDownloadImageBlob } from '../../../utils/zoomedImageShare';
import { DAILY_GUESS_ROWS } from '../../../constants/games/dailyGame';
import { fetchDailyPuzzle } from '../../../utils/dailyApi';
import {
  normalizeDailyShareZoomLevel,
  normalizeDailyZoomOrigin,
} from '../../../utils/games/dailyZoomConfig';
import './DailyPrivateShareCardModal.css';

const FILE_PREFIX = 'zoomed-in-daily';
const SHARE_TEXT = 'Play for free at zoomed-in.com';

const buildFileName = (puzzleDateUtc) => {
  if (puzzleDateUtc) {
    const safeDate = String(puzzleDateUtc).replace(/[^0-9-]+/g, '');
    if (safeDate) return `${FILE_PREFIX}-${safeDate}.png`;
  }
  return `${FILE_PREFIX}.png`;
};

const CopyIcon = () => (
  <svg
    className="daily-private-share-modal__action-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <rect x="4" y="4" width="11" height="11" rx="2" />
  </svg>
);

const ShareIcon = () => (
  <svg
    className="daily-private-share-modal__action-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3v10" />
    <path d="m8 7 4-4 4 4" />
    <path d="M5 13v7h14v-7" />
  </svg>
);

const SaveImageIcon = () => (
  <svg
    className="daily-private-share-modal__action-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 4v10" />
    <path d="m8 10 4 4 4-4" />
    <path d="M5 19h14" />
  </svg>
);

const DailyPrivateShareCardModal = ({
  imageUrl,
  puzzleDateUtc,
  shareZoomLevel,
  shareZoomOrigin,
  guesses,
  solution,
  onClose,
  onHome,
}) => {
  const [cardBlob, setCardBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(true);
  const [busyAction, setBusyAction] = useState('');
  const [showGuessGrid, setShowGuessGrid] = useState(true);
  const [neutralGuessColors, setNeutralGuessColors] = useState(false);
  const [successAction, setSuccessAction] = useState('');
  const [successActionLabel, setSuccessActionLabel] = useState('');
  const [error, setError] = useState('');
  const [resolvedImageUrl, setResolvedImageUrl] = useState(imageUrl);
  const [resolvedShareZoomOrigin, setResolvedShareZoomOrigin] = useState(() =>
    normalizeDailyZoomOrigin(shareZoomOrigin),
  );
  const successTimerRef = useRef(null);

  const fileName = useMemo(() => buildFileName(puzzleDateUtc), [puzzleDateUtc]);
  const normalizedShareZoomOrigin = useMemo(
    () => normalizeDailyZoomOrigin(shareZoomOrigin),
    [shareZoomOrigin],
  );
  const normalizedShareZoomLevel = useMemo(
    () => normalizeDailyShareZoomLevel(shareZoomLevel),
    [shareZoomLevel],
  );

  useEffect(() => {
    setResolvedImageUrl(imageUrl);
    setResolvedShareZoomOrigin(normalizedShareZoomOrigin);
  }, [imageUrl, normalizedShareZoomOrigin]);

  useEffect(() => {
    let mounted = true;
    if (!puzzleDateUtc) return undefined;

    (async () => {
      try {
        const latestPuzzle = await fetchDailyPuzzle();
        if (!mounted) return;
        if (String(latestPuzzle?.dateUtc || '') !== String(puzzleDateUtc || '')) return;

        const latestImageUrl = String(latestPuzzle?.imageUrl || '').trim();
        if (latestImageUrl) {
          setResolvedImageUrl(latestImageUrl);
        }
        setResolvedShareZoomOrigin(
          normalizeDailyZoomOrigin(latestPuzzle?.zoomOrigin, normalizedShareZoomOrigin),
        );
      } catch {
        // Keep local props as fallback if the live refresh fails.
      }
    })();

    return () => {
      mounted = false;
    };
  }, [normalizedShareZoomOrigin, puzzleDateUtc]);

  const canCopyImage = useMemo(
    () =>
      Boolean(
        window?.isSecureContext &&
          navigator?.clipboard?.write &&
          typeof window.ClipboardItem !== 'undefined',
      ),
    [],
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        window.clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  const clearActionSuccess = useCallback(() => {
    if (successTimerRef.current) {
      window.clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
    setSuccessAction('');
    setSuccessActionLabel('');
  }, []);

  const showActionSuccess = useCallback(
    (action, label) => {
      clearActionSuccess();
      setSuccessAction(action);
      setSuccessActionLabel(label);
      successTimerRef.current = window.setTimeout(() => {
        setSuccessAction('');
        setSuccessActionLabel('');
        successTimerRef.current = null;
      }, 1800);
    },
    [clearActionSuccess],
  );

  useEffect(() => {
    let isMounted = true;
    const generate = async () => {
      setIsGenerating(true);
      setError('');
      clearActionSuccess();
      setCardBlob(null);

      try {
        const blob = await createDailyPrivateShareCardBlob({
          imageUrl: resolvedImageUrl,
          puzzleDateUtc,
          zoomLevel: normalizedShareZoomLevel,
          zoomOrigin: resolvedShareZoomOrigin,
          guesses,
          solution,
          includeGuessGrid: showGuessGrid,
          neutralGuessColors,
          maxGuesses: DAILY_GUESS_ROWS,
        });
        if (!isMounted) return;
        setCardBlob(blob);
      } catch (generationError) {
        if (!isMounted) return;
        setError(generationError?.message || 'Failed to generate share card.');
      } finally {
        if (isMounted) {
          setIsGenerating(false);
        }
      }
    };

    generate();

    return () => {
      isMounted = false;
    };
  }, [
    guesses,
    neutralGuessColors,
    normalizedShareZoomLevel,
    puzzleDateUtc,
    resolvedImageUrl,
    resolvedShareZoomOrigin,
    showGuessGrid,
    solution,
    clearActionSuccess,
  ]);

  useEffect(() => {
    if (!cardBlob) {
      setPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(cardBlob);
    setPreviewUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [cardBlob]);

  const handleHome = useCallback(() => {
    if (typeof onHome === 'function') {
      onHome();
      return;
    }
    onClose?.();
  }, [onClose, onHome]);

  const handleCopy = useCallback(async () => {
    if (!cardBlob || busyAction) return;
    setBusyAction('copy');
    setError('');
    clearActionSuccess();

    try {
      if (!canCopyImage) {
        throw new Error(
          window?.isSecureContext
            ? 'Copy image is not supported in this browser.'
            : 'Copy image needs https (or localhost). Use Save Image instead.',
        );
      }

      const textBlob = new Blob([SHARE_TEXT], { type: 'text/plain' });
      const item = new window.ClipboardItem({
        'image/png': cardBlob,
        'text/plain': textBlob,
      });
      await navigator.clipboard.write([item]);
      showActionSuccess('copy', 'Copied!');
    } catch (copyError) {
      setError(copyError?.message || 'Failed to copy image.');
    } finally {
      setBusyAction('');
    }
  }, [busyAction, canCopyImage, cardBlob, clearActionSuccess, showActionSuccess]);

  const handleShare = useCallback(async () => {
    if (!cardBlob || busyAction) return;
    setBusyAction('share');
    setError('');
    clearActionSuccess();

    try {
      const result = await shareOrDownloadImageBlob({
        blob: cardBlob,
        fileName,
        shareTitle: 'Zoomed In Daily',
        shareText: SHARE_TEXT,
      });

      showActionSuccess('share', result?.method === 'share' ? 'Shared!' : 'Saved!');
    } catch (shareError) {
      setError(shareError?.message || 'Failed to share image.');
    } finally {
      setBusyAction('');
    }
  }, [busyAction, cardBlob, fileName, clearActionSuccess, showActionSuccess]);

  const handleSaveImage = useCallback(async () => {
    if (!cardBlob || busyAction) return;
    setBusyAction('save');
    setError('');
    clearActionSuccess();

    try {
      if (typeof window.showSaveFilePicker === 'function') {
        const handle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [
            {
              description: 'PNG image',
              accept: { 'image/png': ['.png'] },
            },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(cardBlob);
        await writable.close();
        showActionSuccess('save', 'Saved!');
        return;
      }

      const file = new File([cardBlob], fileName, {
        type: cardBlob.type || 'image/png',
      });
      if (navigator?.share && navigator?.canShare?.({ files: [file] })) {
        await navigator.share({
          title: 'Save image',
          files: [file],
        });
        showActionSuccess('save', 'Done!');
        return;
      }

      const url = URL.createObjectURL(cardBlob);
      try {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      } finally {
        URL.revokeObjectURL(url);
      }

      showActionSuccess('save', 'Saved!');
    } catch (saveError) {
      if (saveError?.name === 'AbortError') {
        return;
      }
      setError(saveError?.message || 'Failed to save image.');
    } finally {
      setBusyAction('');
    }
  }, [busyAction, cardBlob, fileName, clearActionSuccess, showActionSuccess]);

  return (
    <div className="modal-overlay">
      <div className="modal-content game-result-modal daily-private-share-modal">
        <h2>Share without revealing answers</h2>
        <p className="daily-private-share-modal__subtitle">
          Share a clean Daily card with the image and color pattern only.
        </p>

        <div className="daily-private-share-modal__preview-wrap">
          {isGenerating && (
            <div className="loading-spinner">
              <div className="spinner" />
              <span className="loading-text">Generating share card...</span>
            </div>
          )}

          {!isGenerating && previewUrl && (
            <img
              src={previewUrl}
              alt="Daily private share card preview"
              className="daily-private-share-modal__preview-image"
            />
          )}
        </div>

        <div className="daily-private-share-modal__options">
          <label className="daily-private-share-modal__option">
            <input
              type="checkbox"
              checked={showGuessGrid}
              onChange={(event) => setShowGuessGrid(event.target.checked)}
              disabled={Boolean(busyAction)}
            />
            <span>Show guess grid</span>
          </label>
          <label className="daily-private-share-modal__option">
            <input
              type="checkbox"
              checked={neutralGuessColors}
              onChange={(event) => setNeutralGuessColors(event.target.checked)}
              disabled={!showGuessGrid || Boolean(busyAction)}
            />
            <span>Use neutral colors</span>
          </label>
        </div>

        <div className="daily-private-share-modal__actions">
          <button
            type="button"
            className="daily-private-share-modal__action-btn"
            onClick={handleCopy}
            disabled={!cardBlob || Boolean(busyAction)}
          >
            {busyAction === 'copy' ? (
              'Copying...'
            ) : successAction === 'copy' ? (
              successActionLabel || 'Done!'
            ) : (
              <span className="daily-private-share-modal__action-btn-inner">
                <CopyIcon />
                <span>Copy</span>
              </span>
            )}
          </button>
          <button
            type="button"
            className="daily-private-share-modal__action-btn"
            onClick={handleShare}
            disabled={!cardBlob || Boolean(busyAction)}
          >
            {busyAction === 'share' ? (
              'Sharing...'
            ) : successAction === 'share' ? (
              successActionLabel || 'Done!'
            ) : (
              <span className="daily-private-share-modal__action-btn-inner">
                <ShareIcon />
                <span>Share</span>
              </span>
            )}
          </button>
          <button
            type="button"
            className="daily-private-share-modal__action-btn"
            onClick={handleSaveImage}
            disabled={!cardBlob || Boolean(busyAction)}
          >
            {busyAction === 'save' ? (
              'Saving...'
            ) : successAction === 'save' ? (
              successActionLabel || 'Done!'
            ) : (
              <span className="daily-private-share-modal__action-btn-inner">
                <SaveImageIcon />
                <span>Save Image</span>
              </span>
            )}
          </button>
        </div>

        <div className="daily-private-share-modal__nav">
          <button
            type="button"
            className="daily-private-share-modal__nav-btn daily-private-share-modal__nav-btn--secondary"
            onClick={onClose}
            disabled={Boolean(busyAction)}
          >
            Back
          </button>
          <button
            type="button"
            className="daily-private-share-modal__nav-btn daily-private-share-modal__nav-btn--primary"
            onClick={handleHome}
            disabled={Boolean(busyAction)}
          >
            Home
          </button>
        </div>

        {error && <p className="daily-private-share-modal__error">{error}</p>}
      </div>
    </div>
  );
};

export default DailyPrivateShareCardModal;
